import React, { useEffect, useState } from 'react';

import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';

import { Box, Button, Container, GridLegacy as Grid, Paper, Typography } from '@mui/material';

import { ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER, ROLE_ICS_STORE_TEAM } from '~/consts/roles';

import useCurrentTenant from '~/hooks/useCurrentTenant';

import { generateTripDebugData, getTripDetails } from '~/services/TripPlannerService';
import UsersService from '~/services/UsersService';

import PermissionedComponent from '../Common/PermissionedComponent';

import TripSpoofDialog from './components/TripSpoofDialog';

interface LocationBias {
  lat: number;
  lng: number;
  radius: number;
}

interface ItineraryItem {
  name: string;
  placeName: string;
  locationBias: LocationBias;
  description: string;
  date: string;
  insiderTip: string;
  experienceId: string;
}

interface SelectedItem {
  selected: boolean;
}

interface Theme extends SelectedItem {
  theme: string;
}

interface MustDoActivity extends SelectedItem {
  activity: string;
}

interface ActivityBudget extends SelectedItem {
  activityBudget: string;
}

interface FoodStyle extends SelectedItem {
  foodStyle: string;
}

interface DietaryRequirement extends SelectedItem {
  dietaryRequirement: string;
}

interface TripPace extends SelectedItem {
  tripPace: string;
}

interface TripPlanEntry {
  modelUsed: string;
  type: string;
  isStructured: boolean;
  prompt: string;
  response: string;
  themesSelected?: Array<Theme>;
  mustDoActivitiesSelected?: Array<MustDoActivity>;
  activityBudgetSelected?: Array<ActivityBudget>;
  foodStyleSelected?: Array<FoodStyle>;
  dietaryRequirementSelected?: Array<DietaryRequirement>;
  tripPaceSelected?: Array<TripPace>;
  createdAt: string;
  updatedAt: string;
}

function CreateTripPage() {
  const { tripId } = useParams<{ tripId: string }>();
  const [tripDebugData, setTripDebugData] = useState<Array<TripPlanEntry> | null>(null);
  const [tripDetails, setTripDetails] = useState<Partial<App.TripDetails>>({});
  const [spoofUrl, setSpoofUrl] = useState<string>('');
  const [trip, setTrip] = useState<string>('');
  const [spoofUrlCopied, setSpoofUrlCopied] = useState<boolean>(false);
  const [openSpoofingModal, setOpenSpoofingModal] = useState(false);
  const { tenant: currentTenant } = useCurrentTenant();

  useEffect(() => {
    async function fetchData() {
      try {
        const response = await generateTripDebugData(tripId);
        if (response.status === 200) {
          setTripDebugData(response.result);
        }
        const tripDetailsResponse = await getTripDetails(tripId);
        if (tripDetailsResponse.status === 200) {
          setTripDetails(tripDetailsResponse.result);
        }
      } catch (error) {
        console.error('Error fetching debug data:', error);
      }
    }
    fetchData();
  }, [tripId]);

  const handleSpoofTrip = async () => {
    flushSpoofingState();
    if (!tripDetails.createdBy) {
      return;
    }

    try {
      const spoofedUrl = await UsersService.spoofUserTrip(
        tripDetails.createdBy,
        currentTenant,
        `/trip-planner/trip/${tripId}/summary`,
      );
      let copiedToClipboard = false;
      try {
        await navigator.clipboard.writeText(spoofedUrl);
        copiedToClipboard = true;
      } catch {
        console.error('Failed to copy spoof link to the clipboard. You can still copy it manually from the dialog.');
      }
      setSpoofUrl(spoofedUrl);
      setTrip(tripDetails.name ?? '');
      setSpoofUrlCopied(copiedToClipboard);
      setOpenSpoofingModal(true);
    } catch (error) {
      console.error('Error spoofing trip:', error);
    }
  };

  const flushSpoofingState = () => {
    setSpoofUrl('');
    setTrip('');
    setSpoofUrlCopied(false);
    setOpenSpoofingModal(false);
  };

  const formatResponse = (response: string) => {
    try {
      const tripDetails = JSON.parse(response);
      if (tripDetails.itinerary) {
        return (
          <Box>
            {tripDetails.itinerary.map((item: ItineraryItem, index: number) => (
              <Paper key={index} elevation={1} sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" color="primary" gutterBottom>
                  Name: {item.name}
                </Typography>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Date: {item.date}
                </Typography>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Place Name: {item.placeName}
                </Typography>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  ExperienceId: {item.experienceId}
                </Typography>
                <Typography paragraph>{item.description}</Typography>
                {item.insiderTip && (
                  <Box sx={{ mt: 1, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
                    <Typography variant="subtitle2" color="primary">
                      Insider Tip:
                    </Typography>
                    <Typography variant="body2">{item.insiderTip}</Typography>
                  </Box>
                )}
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Location: {item.locationBias.lat.toFixed(4)}, {item.locationBias.lng.toFixed(4)}
                  </Typography>
                </Box>
              </Paper>
            ))}
          </Box>
        );
      }
      return (
        <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{JSON.stringify(tripDetails, null, 2)}</pre>
      );
    } catch (e) {
      return <Typography sx={{ whiteSpace: 'pre-wrap' }}>{response}</Typography>;
    }
  };

  if (!tripDebugData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography variant="h6">No data available for trip ID: {tripId}</Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      <Helmet>
        <title>Trip prompts</title>
      </Helmet>
      <PermissionedComponent requiredRoles={[ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER, ROLE_ICS_STORE_TEAM]}>
        <Button variant="contained" color="primary" onClick={handleSpoofTrip} sx={{ mb: 2 }}>
          Spoof
        </Button>
      </PermissionedComponent>
      {tripDebugData.map((item: TripPlanEntry, index: number) => (
        <Box key={index} sx={{ mb: 4, p: 2, border: '1px solid #eee', borderRadius: 1 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Input {index + 1}
          </Typography>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Model Used:
                </Typography>
                <Typography>{item.modelUsed}</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Type:
                </Typography>
                <Typography>{item.type}</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Is Structured:
                </Typography>
                <Typography>{item.isStructured ? 'Yes' : 'No'}</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Selected Activity Budget:
                </Typography>
                {item.activityBudgetSelected
                  ?.filter((budget) => budget.selected)
                  .map((budget, i) => (
                    <Typography key={i}>• {budget.activityBudget}</Typography>
                  ))}
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Selected Food Style:
                </Typography>
                {item.foodStyleSelected
                  ?.filter((food) => food.selected)
                  .map((food, i) => (
                    <Typography key={i}>• {food.foodStyle}</Typography>
                  ))}
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Selected Trip Pace:
                </Typography>
                {item.tripPaceSelected
                  ?.filter((pace) => pace.selected)
                  .map((pace, i) => (
                    <Typography key={i}>• {pace.tripPace}</Typography>
                  ))}
              </Box>
            </Grid>
          </Grid>

          {item.themesSelected?.some((theme) => theme.selected) && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                Selected Themes:
              </Typography>
              {item.themesSelected
                .filter((theme) => theme.selected)
                .map((theme, i) => (
                  <Typography key={i}>• {theme.theme}</Typography>
                ))}
            </Box>
          )}

          {item.mustDoActivitiesSelected?.some((activity) => activity.selected) && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                Selected Must Do Activities:
              </Typography>
              {item.mustDoActivitiesSelected
                .filter((activity) => activity.selected)
                .map((activity, i) => (
                  <Typography key={i}>• {activity.activity}</Typography>
                ))}
            </Box>
          )}

          {item.dietaryRequirementSelected?.some((diet) => diet.selected) && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                Selected Dietary Requirements:
              </Typography>
              {item.dietaryRequirementSelected
                .filter((diet) => diet.selected)
                .map((diet, i) => (
                  <Typography key={i}>• {diet.dietaryRequirement}</Typography>
                ))}
            </Box>
          )}

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 2, height: '100%' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                  Prompt:
                </Typography>
                <Typography sx={{ whiteSpace: 'pre-wrap' }}>{item.prompt}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 2, height: '100%' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                  Response:
                </Typography>
                {formatResponse(item.response)}
              </Paper>
            </Grid>
          </Grid>

          <Box sx={{ mt: 2, color: 'text.secondary' }}>
            <Typography variant="caption">Created: {new Date(item.createdAt).toLocaleString()}</Typography>
            <Typography variant="caption" sx={{ display: 'block' }}>
              Updated: {new Date(item.updatedAt).toLocaleString()}
            </Typography>
          </Box>
        </Box>
      ))}
      <PermissionedComponent requiredRoles={[ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER, ROLE_ICS_STORE_TEAM]}>
        <TripSpoofDialog
          openModal={openSpoofingModal}
          flushSpoofingState={flushSpoofingState}
          spoofUrl={spoofUrl}
          trip={trip}
          copiedToClipboard={spoofUrlCopied}
        />
      </PermissionedComponent>
    </Container>
  );
}

export default CreateTripPage;
